import React, { useEffect, useState } from 'react'
// import { useDispatch } from 'react-redux'

import Button, { ButtonRow } from './button'
import styles from '../component-styles/welcome-rhs.module.scss'
import heart from '../assets/heart.svg'
import happy from '../assets/emoji-happy.svg'
import meh from '../assets/emoji-meh.svg'
import sad from '../assets/emoji-sad.svg'
import backArrow from '../assets/back-arrow-grey.svg'
import { FieldSlot, LabelSlot } from './row'
import Input, { Label } from './input'
import TrustpilotCollect from './trustpilot'
import { sendFeedback } from '../api-client/graphql-client'
import { logAction } from '../api-client/analytics-client'
import { Preloader } from './loader'

interface FeedbackFull {
  type: string
  prompt: string
  state: string
  response: string
  label?: string
}

interface FeedbackProps {
  updateFeedback: (
    state: string,
    label: string | null,
    response: string | null,
  ) => void
}

function YesNo({ updateFeedback }: FeedbackProps): React.ReactElement {
  return (
    <>
      <h2>
        We{' '}
        <span className={styles.heart}>
          <img src={heart} alt="love" />
        </span>{' '}
        your feedback
      </h2>
      <p>Did you achieve what you set out to do today?</p>
      <ButtonRow style={{ justifyContent: 'left', gap: '16px' }} centerAlign>
        <Button
          type="upvote"
          onClick={() => {
            updateFeedback('trustpilot', null, 'Yes')
          }}
        >
          Yes
        </Button>
        <Button
          type="downvote"
          onClick={() => {
            updateFeedback('feedback', 'How could we improve?', 'No')
          }}
        >
          No
        </Button>
      </ButtonRow>
    </>
  )
}

function Emoji({ updateFeedback }: FeedbackProps): React.ReactElement {
  return (
    <>
      <h2>How did you feel using Uplifter today?</h2>
      <ButtonRow style={{ justifyContent: 'center' }} centerAlign>
        <Button
          type="image"
          onClick={() => {
            updateFeedback('trustpilot', null, 'Happy')
          }}
          className={styles.emoji}
        >
          <img src={happy} alt="Happy" />
        </Button>
        <Button
          type="image"
          onClick={() => {
            updateFeedback(
              'feedback',
              'Please let us know where we could improve.',
              'Neutral',
            )
          }}
          className={styles.emoji}
        >
          <img src={meh} alt="Meh" />
        </Button>
        <Button
          type="image"
          onClick={() => {
            updateFeedback(
              'feedback',
              'Please let us know where we could improve.',
              'Frustrated',
            )
          }}
          className={styles.emoji}
        >
          <img src={sad} alt="Sad" />
        </Button>
      </ButtonRow>
    </>
  )
}

function ReportSuggestAsk({
  updateFeedback,
}: FeedbackProps): React.ReactElement {
  return (
    <>
      <h2>
        We{' '}
        <span className={styles.heart}>
          <img src={heart} alt="love" />
        </span>{' '}
        your feedback
      </h2>
      <div style={{ textAlign: 'left' }}>
        <Button
          type="link"
          icon="warning"
          onClick={() => {
            updateFeedback(
              'feedback',
              'Please let us know what the issue was.',
              'Report a problem',
            )
          }}
          className={styles.reportSuggest}
        >
          Report a problem
        </Button>
        <Button
          type="link"
          icon="plus"
          onClick={() => {
            updateFeedback(
              'feedback',
              'What could we do better?',
              'Suggest an improvement',
            )
          }}
          className={styles.reportSuggest}
        >
          Suggest an improvement
        </Button>
        <Button
          type="link"
          icon="question"
          onClick={() => {
            updateFeedback('feedback', 'How can we help?', 'Ask a question')
          }}
          className={styles.ask}
        >
          Ask a question
        </Button>
        <Button
          type="link"
          icon="write"
          onClick={() => {
            window.open(
              'https://uk.trustpilot.com/review/uplifter.ai',
              '_blank',
            )
          }}
          className={styles.ask}
        >
          Leave a review
        </Button>
      </div>
    </>
  )
}

export default function WelcomeFeedback(): React.ReactElement {
  const [feedback, setFeedback] = useState<FeedbackFull | null>(null)
  const [feedbackInput, setFeedbackInput] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const feedbackStorage = sessionStorage.getItem('feedback')
    if (feedbackStorage === null) {
      const newFeedback: FeedbackFull = {
        type: ['yesNo', 'reportSuggestAsk', 'emoji'][
          Math.floor(Math.random() * 3)
        ],
        state: 'initial',
        prompt: '',
        response: '',
      }
      switch (newFeedback.type) {
        case 'yesNo':
          newFeedback.prompt = 'Did you achieve what you set out to do today?'
          break
        case 'reportSuggestAsk':
          newFeedback.prompt = 'Report/Suggest/Ask'
          break
        case 'emoji':
          newFeedback.prompt = 'How did you feel using Uplifter today?'
          break
        default:
          break
      }
      sessionStorage.setItem('feedback', JSON.stringify(newFeedback))
      setFeedback(newFeedback)
    } else {
      setFeedback(JSON.parse(feedbackStorage))
    }
  }, [])

  const updateFeedback = (
    state: string,
    label: string | null,
    response: string | null,
  ): void => {
    if (feedback) {
      const newFeedback = { ...feedback, state }
      if (label) {
        newFeedback.label = label
      }
      if (response) {
        newFeedback.response = response
      }
      sessionStorage.setItem('feedback', JSON.stringify(newFeedback))
      setFeedback(newFeedback)
    }
  }

  if (feedback === null) {
    return <></>
  }

  if (feedback.state === 'submitted') {
    return (
      <div className={styles.feedbackContainer}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ marginBottom: 0 }}>Thanks for the feedback!</h2>
          <Button
            type="image"
            onClick={() => {
              updateFeedback('feedback', null, null)
            }}
            className={styles.backArrow}
            style={{ marginBottom: 0 }}
          >
            <img src={backArrow} alt="Back" />
          </Button>
        </div>
      </div>
    )
  }

  if (feedback.state === 'trustpilot') {
    return (
      <div className={styles.feedbackContainer}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ marginLeft: 0 }}>
            We{' '}
            <span className={styles.heart}>
              <img src={heart} alt="love" />
            </span>{' '}
            your feedback
          </h2>
          <Button
            type="image"
            onClick={() => {
              updateFeedback('initial', null, null)
            }}
            className={styles.backArrow}
          >
            <img src={backArrow} alt="Back" />
          </Button>
        </div>
        <p>Please share your experience on Trustpilot.</p>
        <TrustpilotCollect
          templateID="56278e9abfbbba0bdcd568bc"
          businessUnitID="61e6d3fd0f84079c6cb14f03"
          width="100%"
          height="52px"
        />
      </div>
    )
  }

  if (feedback.state === 'feedback') {
    return (
      <div className={styles.feedbackContainer}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ marginLeft: 0 }}>
            We{' '}
            <span className={styles.heart}>
              <img src={heart} alt="love" />
            </span>{' '}
            your feedback
          </h2>
          <Button
            type="image"
            onClick={() => {
              updateFeedback('initial', null, null)
            }}
            className={styles.backArrow}
          >
            <img src={backArrow} alt="Back" />
          </Button>
        </div>
        <form
          className={styles.form}
          onSubmit={async (e) => {
            e.preventDefault()

            setLoading(true)
            setError('')

            try {
              await sendFeedback({
                message: feedbackInput,
                page: '/welcome',
                feedbackContext: {
                  feedbackPrompt: feedback.prompt,
                  feedbackResponse: feedback.response,
                },
              })
              const actionData = {
                action: 'send-feedback-welcome',
                longDescription: `{"prompt": "${feedback.prompt}", "response": "${feedback.response}", "message": "${feedbackInput}"}`,
                websiteSection: 'welcome',
              }
              await logAction(actionData)
              updateFeedback('submitted', null, null)
            } catch (err) {
              setError('We ran into a problem. Please try again later')
            } finally {
              setLoading(false)
            }
          }}
        >
          {feedback.label && (
            <LabelSlot noPadding column>
              <Label id="feedbackInput">{feedback.label}</Label>
            </LabelSlot>
          )}
          <FieldSlot noPadding column>
            <Input
              name="feedbackInput"
              type="textArea"
              label="Feedback Input"
              value={feedbackInput}
              onValueChange={(val) => {
                setFeedbackInput(val)
              }}
              required
              placeholder="Enter feedback"
              multilineInput
              textAreaHeight={80}
              lineBreakAll={false}
            />
            {error && (
              <div className={styles.formMessages}>
                <p className={styles.error}>{error}</p>
              </div>
            )}
          </FieldSlot>
          <FieldSlot noPadding column>
            {loading ? (
              <Preloader style={{ width: 40, height: 25 }} />
            ) : (
              <Button disabled={false} submit type="container">
                Send
              </Button>
            )}
          </FieldSlot>
        </form>
      </div>
    )
  }

  switch (feedback.type) {
    case 'yesNo':
      return (
        <div className={styles.feedbackContainer}>
          <YesNo updateFeedback={updateFeedback} />
        </div>
      )
    case 'emoji':
      return (
        <div className={styles.feedbackContainer}>
          <Emoji updateFeedback={updateFeedback} />
        </div>
      )
    case 'reportSuggestAsk':
      return (
        <div className={styles.feedbackContainer}>
          <ReportSuggestAsk updateFeedback={updateFeedback} />
        </div>
      )
    default:
      return <></>
  }
}
